import { useTranslation } from 'react-i18next';
import { TenantSelectionEnum } from '../../redux/session/session.slice';
import { colors } from '../../styles/color-tokens';
import { Select as MuiSelect, MenuItem as MuiMenuItem, SelectProps } from '@mui/material';
import { BsChevronDown } from 'react-icons/bs';
import { TenantUserRoleReadModel } from '@client/api';

export type TenantsDropdownVariant = 'filled' | 'standard';

export interface TenantsDropdownProps extends Omit<SelectProps, 'variant'> {
  items: TenantUserRoleReadModel[];
  className?: string;
  variant?: TenantsDropdownVariant;
  menuItemFontSize?: string;
  showBorderAfter?: boolean;
}

const getMenuProps = (variant: TenantsDropdownVariant) => ({
  transitionDuration: 75,
  PaperProps: {
    sx: {
      backgroundColor: '#201E1A',
      minWidth: variant === 'filled' ? '221px !important' : undefined,
      borderRadius: variant === 'filled' ? 0 : 1,
      marginTop: variant === 'standard' ? 1 : undefined,
      '& .MuiList-root': {
        padding: 0,
      },
      '& .MuiMenuItem-root': {
        fontSize: variant === 'filled' ? '12px' : '14px',
        color: '#FFFFFF',
        paddingTop: '12px',
        paddingBottom: '12px',
        paddingLeft: '16px',
        paddingRight: '16px',
        borderBottom: '1px solid #322F2C',
        '&:last-child': {
          borderBottom: 'none',
        },
        '&:hover': {
          backgroundColor: '#272521',
        },
        '&.Mui-selected': {
          backgroundColor: '#272521 !important',
          '&:hover': {
            backgroundColor: '#272521 !important',
          },
          '&:focus': {
            backgroundColor: '#272521 !important',
          },
        },
      },
    },
  },
});

const getSelectSx = (variant: TenantsDropdownVariant, showBorderAfter: boolean) => {
  const baseSx = {
    '&:hover::before': {
      borderBottom: 'none !important',
    },
    '& .MuiSelect-icon': {
      color: colors.text.primary,
      fontSize: '0.75rem',
      right: '12px',
    },
    '&.Mui-disabled .MuiSelect-icon': {
      display: 'none',
    },
    '&::before': {
      borderBottom: 'none',
    },
    '& .MuiOutlinedInput-notchedOutline': {
      border: 'none',
    },
    '& .MuiSelect-select.Mui-disabled': {
      WebkitTextFillColor: `${colors.text.primary}`,
    },
  };

  if (variant === 'filled') {
    return {
      ...baseSx,
      '&:hover': {
        backgroundColor: 'transparent',
      },
      '& .MuiSelect-select': {
        paddingLeft: 2,
        fontSize: '14px',
      },
      '& .MuiInputLabel-root': {
        color: colors.text.secondary,
      },
      color: colors.primary.main,
      '&::after': showBorderAfter
        ? {
            borderBottom: `2px solid ${colors.primary.main} !important`,
          }
        : {
            borderBottom: 'none',
          },
    };
  }

  // standard variant
  return {
    ...baseSx,
    backgroundColor: '#2A2722',
    borderRadius: 1,
    '&:hover': {
      backgroundColor: '#2A2722',
    },
    '& .MuiSelect-select': {
      paddingTop: '12px',
      paddingBottom: '12px',
      paddingLeft: '16px',
      fontSize: '14px',
      color: '#FFFFFF',
    },
    '&::after': {
      borderBottom: 'none',
    },
  };
};

export const TenantsDropdown = ({
  items,
  className,
  variant = 'filled',
  showBorderAfter = true,
  ...props
}: TenantsDropdownProps) => {
  const { t } = useTranslation('common');

  return (
    <MuiSelect
      IconComponent={BsChevronDown}
      className={className}
      variant={variant}
      MenuProps={getMenuProps(variant)}
      sx={getSelectSx(variant, showBorderAfter)}
      inputProps={{ 'data-testid': 'tenants-selection-dropdown' }}
      disabled={items.length <= 1}
      {...props}
    >
      <MuiMenuItem className={variant === 'filled' ? 'text-primary' : undefined} value={TenantSelectionEnum.All}>
        {t('all')}
      </MuiMenuItem>
      {items.map((tenant) => (
        <MuiMenuItem key={tenant.id} value={tenant.id}>
          {tenant.name ?? tenant.id}
        </MuiMenuItem>
      ))}
    </MuiSelect>
  );
};
